import { MantineThemeOverride, MantineTheme } from "@mantine/core";

// Mantine color tuple type
type MantineColorTuple = [
  string,
  string,
  string,
  string,
  string,
  string,
  string,
  string,
  string,
  string,
];

// Refined color palette
const gamingColors: Record<string, MantineColorTuple> = {
  // Primary accent - Cyan
  cyan: [
    "#e0fcff",
    "#bef8fd",
    "#87eaf2",
    "#54d1db",
    "#38bec9",
    "#2cb1bc",
    "#14919b",
    "#0e7c86",
    "#0a6c74",
    "#014d40",
  ],

  // Secondary accent - Purple
  purple: [
    "#f3e8ff",
    "#e9d5ff",
    "#d8b4fe",
    "#c084fc",
    "#a855f7",
    "#9333ea",
    "#7c3aed",
    "#6d28d9",
    "#5b21b6",
    "#4c1d95",
  ],

  // Green (success)
  green: [
    "#ecfdf5",
    "#d1fae5",
    "#a7f3d0",
    "#6ee7b7",
    "#34d399",
    "#10b981",
    "#059669",
    "#047857",
    "#065f46",
    "#064e3b",
  ],

  // Warning - Amber
  amber: [
    "#fffbeb",
    "#fef3c7",
    "#fde68a",
    "#fcd34d",
    "#fbbf24",
    "#f59e0b",
    "#d97706",
    "#b45309",
    "#92400e",
    "#78350f",
  ],

  // Error - Rose
  rose: [
    "#fff1f2",
    "#ffe4e6",
    "#fecdd3",
    "#fda4af",
    "#fb7185",
    "#f43f5e",
    "#e11d48",
    "#be123c",
    "#9f1239",
    "#881337",
  ],

  // Dark backgrounds
  dark: [
    "#C1C2C5",
    "#A6A7AB",
    "#909296",
    "#5C5F66",
    "#373A40",
    "#2C2E33",
    "#25262B",
    "#1A1B1E",
    "#141517",
    "#101113",
  ],
};

// Animation timing functions
const transitions = {
  fast: "150ms cubic-bezier(0.4, 0, 0.2, 1)",
  normal: "250ms cubic-bezier(0.4, 0, 0.2, 1)",
  slow: "350ms cubic-bezier(0.4, 0, 0.2, 1)",
};

export const theme: MantineThemeOverride = {
  colors: gamingColors,

  primaryColor: "cyan",
  primaryShade: { light: 6, dark: 5 },

  // Typography - distinctive font pairing
  fontFamily:
    "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  fontFamilyMonospace: "'JetBrains Mono', 'Fira Code', monospace",
  headings: {
    fontFamily:
      "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    fontWeight: "700",
    sizes: {
      h1: { fontSize: "2.5rem", lineHeight: "1.2" },
      h2: { fontSize: "2rem", lineHeight: "1.25" },
      h3: { fontSize: "1.5rem", lineHeight: "1.3" },
      h4: { fontSize: "1.25rem", lineHeight: "1.35" },
      h5: { fontSize: "1rem", lineHeight: "1.4" },
      h6: { fontSize: "0.875rem", lineHeight: "1.45" },
    },
  },

  // Radius - softer, modern feel
  radius: {
    xs: "0.25rem",
    sm: "0.375rem",
    md: "0.5rem",
    lg: "0.75rem",
    xl: "1rem",
  },

  // Spacing
  spacing: {
    xs: "0.5rem",
    sm: "0.75rem",
    md: "1rem",
    lg: "1.5rem",
    xl: "2rem",
  },

  defaultRadius: "lg",

  // Pure opacity-based shadows - no colored tints
  shadows: {
    xs: "0 1px 2px rgba(0, 0, 0, 0.3)",
    sm: "0 2px 4px rgba(0, 0, 0, 0.4)",
    md: "0 4px 8px rgba(0, 0, 0, 0.5)",
    lg: "0 8px 16px rgba(0, 0, 0, 0.5)",
    xl: "0 12px 24px rgba(0, 0, 0, 0.6)",
  },

  components: {
    Table: {
      defaultProps: {
        striped: true,
        highlightOnHover: true,
      },
      styles: (theme: any) => ({
        root: {
          minHeight: "200px",
        },
        // Support striped/hover for raw HTML <tr> elements (v6 compat)
        table: {
          "& thead tr": {
            borderBottom: `1px solid ${theme.colors.dark[4]}`,
          },
          "& tbody tr:nth-of-type(odd)": {
            backgroundColor: theme.colors.dark[6],
          },
          "& tbody tr:hover": {
            backgroundColor: theme.colors.dark[5],
          },
        },
      }),
    },

    Button: {
      defaultProps: {
        radius: "lg",
      },
      styles: () => ({
        root: {
          fontWeight: 600,
          letterSpacing: "0.025em",
          transition: transitions.fast,
        },
      }),
    },

    ActionIcon: {
      defaultProps: {
        radius: "lg",
      },
      styles: (theme: any) => ({
        root: {
          transition: transitions.fast,
          "&:hover": {
            backgroundColor: theme.colors.dark[5],
          },
        },
      }),
    },

    TextInput: {
      defaultProps: {
        radius: "lg",
      },
      styles: (theme: any) => ({
        input: {
          backgroundColor: theme.colors.dark[7],
          border: `1px solid ${theme.colors.dark[4]}`,
          transition: transitions.fast,
          "&:focus": {
            borderColor: theme.colors.cyan[5],
          },
          "&::placeholder": {
            color: theme.colors.dark[3],
          },
        },
      }),
    },

    NumberInput: {
      defaultProps: {
        radius: "lg",
      },
      styles: (theme: any) => ({
        input: {
          backgroundColor: theme.colors.dark[7],
          border: `1px solid ${theme.colors.dark[4]}`,
          transition: transitions.fast,
          "&:focus": {
            borderColor: theme.colors.cyan[5],
          },
        },
      }),
    },

    Textarea: {
      defaultProps: {
        radius: "lg",
      },
      styles: (theme: any) => ({
        input: {
          backgroundColor: theme.colors.dark[7],
          border: `1px solid ${theme.colors.dark[4]}`,
          transition: transitions.fast,
          "&:focus": {
            borderColor: theme.colors.cyan[5],
          },
        },
      }),
    },

    Select: {
      defaultProps: {
        radius: "lg",
      },
      styles: (theme: any) => ({
        input: {
          backgroundColor: theme.colors.dark[7],
          border: `1px solid ${theme.colors.dark[4]}`,
          transition: transitions.fast,
          "&:focus": {
            borderColor: theme.colors.cyan[5],
          },
        },
        dropdown: {
          backgroundColor: theme.colors.dark[7],
          border: `1px solid ${theme.colors.dark[4]}`,
        },
        item: {
          "&[data-selected]": {
            backgroundColor: theme.colors.cyan[7],
          },
          "&[data-hovered]": {
            backgroundColor: theme.colors.dark[5],
          },
        },
      }),
    },

    MultiSelect: {
      defaultProps: {
        radius: "lg",
      },
      styles: (theme: any) => ({
        input: {
          backgroundColor: theme.colors.dark[7],
          border: `1px solid ${theme.colors.dark[4]}`,
          transition: transitions.fast,
          "&:focus-within": {
            borderColor: theme.colors.cyan[5],
          },
        },
        value: {
          backgroundColor: theme.colors.cyan[8],
        },
      }),
    },

    Checkbox: {
      styles: (theme: any) => ({
        input: {
          backgroundColor: theme.colors.dark[7],
          border: `1px solid ${theme.colors.dark[4]}`,
          transition: transitions.fast,
          "&:checked": {
            backgroundColor: theme.colors.cyan[6],
            borderColor: theme.colors.cyan[6],
          },
        },
      }),
    },

    Modal: {
      defaultProps: {
        radius: "lg",
        overlayProps: {
          blur: 3,
          opacity: 0.7,
        },
      },
      styles: (theme: any) => ({
        content: {
          backgroundColor: theme.colors.dark[7],
          border: `1px solid ${theme.colors.dark[4]}`,
        },
        header: {
          backgroundColor: theme.colors.dark[7],
        },
        title: {
          fontWeight: 600,
        },
      }),
    },

    Card: {
      defaultProps: {
        radius: "lg",
      },
      styles: (theme: any) => ({
        root: {
          backgroundColor: theme.colors.dark[7],
          border: `1px solid ${theme.colors.dark[5]}`,
          transition: transitions.normal,
        },
      }),
    },

    Paper: {
      defaultProps: {
        radius: "lg",
      },
      styles: (theme: any) => ({
        root: {
          backgroundColor: theme.colors.dark[7],
        },
      }),
    },

    Badge: {
      defaultProps: {
        radius: "sm",
      },
      styles: () => ({
        root: {
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
        },
      }),
    },

    Notification: {
      defaultProps: {
        radius: "lg",
      },
      styles: (theme: any) => ({
        root: {
          backgroundColor: theme.colors.dark[7],
          border: `1px solid ${theme.colors.dark[4]}`,
        },
      }),
    },

    Tooltip: {
      defaultProps: {
        radius: "sm",
      },
      styles: (theme: any) => ({
        tooltip: {
          backgroundColor: theme.colors.dark[6],
          border: `1px solid ${theme.colors.dark[4]}`,
          color: theme.colors.dark[0],
          fontSize: theme.fontSizes.sm,
        },
      }),
    },

    Menu: {
      styles: (theme: any) => ({
        dropdown: {
          backgroundColor: theme.colors.dark[7],
          border: `1px solid ${theme.colors.dark[4]}`,
        },
        item: {
          transition: transitions.fast,
          "&[data-hovered]": {
            backgroundColor: theme.colors.dark[5],
          },
        },
      }),
    },

    Tabs: {
      styles: (theme: any) => ({
        tab: {
          fontWeight: 500,
          transition: transitions.fast,
          "&[data-active]": {
            borderColor: theme.colors.cyan[5],
            color: theme.colors.cyan[4],
          },
          "&:hover": {
            backgroundColor: theme.colors.dark[6],
          },
        },
      }),
    },

    NavLink: {
      styles: (theme: any) => ({
        root: {
          borderRadius: theme.radius.md,
          transition: transitions.fast,
          "&[data-active]": {
            backgroundColor: theme.colors.cyan[9],
            borderLeft: `3px solid ${theme.colors.cyan[5]}`,
          },
          "&:hover": {
            backgroundColor: theme.colors.dark[6],
          },
        },
      }),
    },

    Anchor: {
      styles: (theme: any) => ({
        root: {
          color: theme.colors.cyan[4],
          transition: transitions.fast,
          "&:hover": {
            color: theme.colors.cyan[3],
          },
        },
      }),
    },

    Progress: {
      styles: (theme: any) => ({
        root: {
          backgroundColor: theme.colors.dark[5],
        },
      }),
    },

    Skeleton: {
      styles: (theme: any) => ({
        root: {
          "&::before": {
            background: theme.colors.dark[6],
          },
          "&::after": {
            background: `linear-gradient(90deg, transparent, ${theme.colors.dark[4]}, transparent)`,
          },
        },
      }),
    },

    Loader: {
      defaultProps: {
        color: "cyan",
      },
    },

    Divider: {
      styles: (theme: any) => ({
        root: {
          borderColor: theme.colors.dark[5],
        },
      }),
    },

    Alert: {
      defaultProps: {
        radius: "lg",
      },
      styles: () => ({
        root: {
          border: "1px solid",
        },
      }),
    },

    Code: {
      styles: (theme: any) => ({
        root: {
          backgroundColor: theme.colors.dark[6],
          border: `1px solid ${theme.colors.dark[4]}`,
          color: theme.colors.cyan[4],
        },
      }),
    },

    Drawer: {
      defaultProps: {
        overlayProps: {
          blur: 2,
          opacity: 0.6,
        },
      },
      styles: (theme: any) => ({
        content: {
          backgroundColor: theme.colors.dark[7],
        },
      }),
    },
  },
};

// Export utilities for use in components
export const gamingThemeUtils = {
  transitions,
  colors: gamingColors,
};

// Status color mapping for badges
export const statusColors: Record<string, string> = {
  active: "green",
  live: "green",
  online: "green",
  success: "green",
  completed: "green",
  pending: "amber",
  waiting: "amber",
  warning: "amber",
  draft: "dark",
  inactive: "dark",
  offline: "dark",
  error: "rose",
  failed: "rose",
  cancelled: "rose",
  deleted: "rose",
};

export type GamingColorKey = keyof typeof gamingColors;
